// Presentational panels for Account → Gumroad bundle tab.
// Mirrors Transfer success/error boxes and Settings section grouping.

import { RevoltButton, RevoltButtonLink } from '../../../components';
import {
  GUMROAD_BUNDLE_HELP_CONTEXT,
  GUMROAD_BUNDLE_MARKETPLACE_CTA_HINT,
  GUMROAD_BUNDLE_MARKETPLACE_CTA_LABEL,
  GUMROAD_BUNDLE_PANEL_COPY,
  resolveGumroadBundleSectionIntro,
  GUMROAD_BUNDLE_SUPPORT_DISCORD_CHANNELS,
} from './gumroadBundleUiConfig';

/**
 * @param {{ uiState: string, status?: { code?: string } | null }} props
 */
export function GumroadBundleSectionIntro({ uiState, status = null }) {
  const intro = resolveGumroadBundleSectionIntro(uiState, status);
  if (!intro) return null;
  return <p className="gumroad-bundle-section-intro">{intro}</p>;
}

export function GumroadBundleLoadingState() {
  return (
    <div className="gumroad-bundle-loading" role="status">
      <div className="gumroad-bundle-loading-spinner" aria-hidden>
        <i className="fa-solid fa-spinner fa-spin" />
      </div>
      <p className="gumroad-bundle-loading-text">{GUMROAD_BUNDLE_PANEL_COPY.loading}</p>
    </div>
  );
}

/**
 * @param {{ variant: 'success' | 'info' | 'warning' | 'error' | 'neutral', title?: string, lead?: string, children?: import('react').ReactNode, role?: string }} props
 */
export function GumroadBundleStatusPanel({ variant, title, lead, children, role }) {
  return (
    <div
      className={`gumroad-bundle-status-panel gumroad-bundle-status-panel--${variant}`}
      role={role}
    >
      {title ? <h3 className="gumroad-bundle-status-panel__title">{title}</h3> : null}
      {lead ? <p className="gumroad-bundle-status-panel__lead">{lead}</p> : null}
      {children}
    </div>
  );
}

/**
 * @param {{ title: string, children: import('react').ReactNode }} props
 */
export function GumroadBundleStepSection({ title, children }) {
  return (
    <section className="gumroad-bundle-step-section">
      <h3 className="gumroad-bundle-step-section__title">{title}</h3>
      <div className="gumroad-bundle-step-section__body">{children}</div>
    </section>
  );
}

/**
 * @param {{ marketplaceUrl: string | null, variant?: 'primary' | 'outline' }} props
 */
export function GumroadBundleMarketplaceCta({ marketplaceUrl, variant = 'primary' }) {
  if (!marketplaceUrl) return null;
  return (
    <div className="gumroad-bundle-marketplace-cta">
      <RevoltButtonLink
        href={marketplaceUrl}
        target="_blank"
        rel="noreferrer"
        variant={variant}
        className="gumroad-bundle-marketplace-cta__button"
      >
        <i className="fa-solid fa-bag-shopping" aria-hidden />
        {GUMROAD_BUNDLE_MARKETPLACE_CTA_LABEL}
        <i className="fa-solid fa-arrow-up-right-from-square gumroad-bundle-marketplace-cta__external" aria-hidden />
      </RevoltButtonLink>
      <p className="gumroad-bundle-marketplace-cta__hint">{GUMROAD_BUNDLE_MARKETPLACE_CTA_HINT}</p>
    </div>
  );
}

/**
 * @param {{ code: string, onCopy: (code: string) => void, copied: boolean }} props
 */
export function GumroadBundlePromoCodeCard({ code, onCopy, copied }) {
  if (!code) return null;
  return (
    <div className="gumroad-bundle-promo-card">
      <span className="gumroad-bundle-promo-card__label">Promotion code</span>
      <div className="gumroad-bundle-promo-card__row">
        <code className="gumroad-bundle-promo-card__code" aria-label="Promotion code">
          {code}
        </code>
        <RevoltButton
          type="button"
          variant="outline"
          size="sm"
          className="gumroad-bundle-promo-card__copy"
          onClick={() => onCopy(code)}
        >
          <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden />
          {copied ? GUMROAD_BUNDLE_PANEL_COPY.copiedButton : GUMROAD_BUNDLE_PANEL_COPY.copyButton}
        </RevoltButton>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   context?: 'checkout' | 'support' | 'transfer_pending',
 *   marketplaceUrl?: string | null,
 * }} props
 */
export function GumroadBundleHelpPanel({
  context = GUMROAD_BUNDLE_HELP_CONTEXT.SUPPORT,
  marketplaceUrl = null,
}) {
  const helpBody =
    context === GUMROAD_BUNDLE_HELP_CONTEXT.CHECKOUT
      ? GUMROAD_BUNDLE_PANEL_COPY.helpCheckoutBody
      : context === GUMROAD_BUNDLE_HELP_CONTEXT.TRANSFER_PENDING
        ? GUMROAD_BUNDLE_PANEL_COPY.helpTransferPendingBody
        : GUMROAD_BUNDLE_PANEL_COPY.helpSupportBody;

  const showMarketplaceCta = context === GUMROAD_BUNDLE_HELP_CONTEXT.CHECKOUT;

  return (
    <GumroadBundleStepSection title={GUMROAD_BUNDLE_PANEL_COPY.helpTitle}>
      <p className="gumroad-bundle-help__text">{helpBody}</p>
      {showMarketplaceCta ? (
        <GumroadBundleMarketplaceCta marketplaceUrl={marketplaceUrl} variant="outline" />
      ) : null}
      <p className="gumroad-bundle-help__discord">
        {GUMROAD_BUNDLE_PANEL_COPY.helpDiscordLead}{' '}
        {GUMROAD_BUNDLE_SUPPORT_DISCORD_CHANNELS.map((channel, index) => (
          <span key={channel}>
            {index > 0 ? ', ' : ''}
            <code>#{channel}</code>
          </span>
        ))}
      </p>
    </GumroadBundleStepSection>
  );
}

/**
 * @param {{ onNavigate?: () => void }} props
 */
export function GumroadBundleTransferHint({ onNavigate }) {
  return (
    <div className="gumroad-bundle-transfer-hint">
      <div className="gumroad-bundle-transfer-hint__copy">
        <p className="gumroad-bundle-transfer-hint__title">Bought on a different email?</p>
        <p className="gumroad-bundle-transfer-hint__body">{GUMROAD_BUNDLE_PANEL_COPY.transferTabHint}</p>
      </div>
      {onNavigate ? (
        <RevoltButton type="button" variant="outline" size="sm" onClick={onNavigate}>
          <i className="fa-solid fa-right-left" aria-hidden />
          {GUMROAD_BUNDLE_PANEL_COPY.transferTabCta}
        </RevoltButton>
      ) : null}
    </div>
  );
}

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function GumroadBundleActionRow({ children }) {
  return <div className="gumroad-bundle-action-row">{children}</div>;
}
